import React, { useState, useEffect } from "react";
import {
  Text, Button, Table, Thead, Tbody, Tr, Th, Td,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Input, Switch, HStack, Badge, IconButton, useDisclosure,
  FormControl, FormLabel, Select,
} from "@chakra-ui/react";
import { FaTrash, FaPlus, FaPencilAlt } from "react-icons/fa";
import { Card } from "components/Card";
import { H2 } from "components/text/headings";
import { Page } from "components/Page";
import { WithAuthorization } from "context";
import { useAuth } from "context";
import { useToast } from "@chakra-ui/react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import AIProgressWidget from "pages/ISSB/AITasks/AIProgressWidget";
import {
  getModels,
  getAllModelsAdmin, upsertModelAdmin, deleteModelAdmin,
  getSystemDefaults, saveSystemDefaults,
  getEmbeddingConfig, saveEmbeddingConfig, triggerWeaviateReindex,
} from "api/ai/ai.service";

const COMPONENT_IDS = [
  { id: "chat", label: "Chat" },
  { id: "report_writer", label: "Report Writer" },
  // Report planning step (plan_report()), split out from report_writer so it can use a
  // different model - falls back to report_writer's model (org override, then system
  // default) when left unconfigured, so leaving this blank is a safe no-op.
  { id: "report_planner", label: "Report Planner" },
  { id: "question_answer", label: "AI Review (Question Answer)" },
  { id: "fact_checker", label: "Fact Checker" },
  { id: "objectivity_check", label: "Objectivity Check" },
  { id: "user_rep_check", label: "User Satisfaction Check" },
  // Data Mapping Agent (data-sorting service) — system default only, no per-org
  // override exists for this component, so it's intentionally NOT duplicated
  // into AISettingsPage.jsx's COMPONENT_IDS.
  { id: "data_mapping", label: "Data Mapping" },
  // Foundation Data v2 metric extraction. Falls back to question_answer's model (org override, then
  // system default) when left unconfigured, so leaving this blank is a safe no-op — get_llm_client
  // raises when nothing resolves at all, which is what that fallback exists to prevent.
  // System default only, like data_mapping: intentionally NOT duplicated into AISettingsPage.jsx.
  { id: "foundation_extract", label: "Foundation Data Extraction" },
];

const EMPTY_MODEL = {
  name: "",
  label: "",
  provider: "",
  litellm_model_name: "",
  price_per_million_input: "",
  price_per_million_output: "",
  temperature: "0",
  // Blank => send null => backend stores NULL => no max_tokens kwarg sent => provider default
  // (4096) applies. Deliberately NOT defaulted to "0": a literal 0 would cap the completion to
  // nothing and break every call for that model (ESG-3952).
  max_output_tokens: "",
  sort_order: "",
  active: true,
};

function ModelModal({ isOpen, onClose, selectedModel, onSaved }) {
  const { getAccessTokenSilently } = useAuth();
  const toast = useToast();
  const isEditing = selectedModel !== null;
  const [form, setForm] = useState(EMPTY_MODEL);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setErrors({});
      setForm(
        selectedModel
          ? {
            name: selectedModel.name ?? "",
            label: selectedModel.label ?? "",
            provider: selectedModel.provider ?? "",
            litellm_model_name: selectedModel.litellm_model_name ?? "",
            price_per_million_input: selectedModel.price_per_million_input ?? "",
            price_per_million_output: selectedModel.price_per_million_output ?? "",
            temperature: selectedModel.temperature ?? "0",
            max_output_tokens: selectedModel.max_output_tokens ?? "",
            sort_order: selectedModel.sort_order ?? "",
            active: selectedModel.active ?? true,
          }
          : EMPTY_MODEL
      );
    }
  }, [isOpen, selectedModel]);

  const { mutate: save, isLoading } = useMutation(
    async () => {
      const token = await getAccessTokenSilently();
      return upsertModelAdmin(
        {
          ...form,
          price_per_million_input: form.price_per_million_input === "" ? 0 : Number(form.price_per_million_input),
          price_per_million_output: form.price_per_million_output === "" ? 0 : Number(form.price_per_million_output),
          temperature: form.temperature === "" ? 0 : Number(form.temperature),
          // null (not 0) when blank — 0 would cap output to nothing. null => provider default.
          max_output_tokens: form.max_output_tokens === "" ? null : parseInt(form.max_output_tokens, 10),
          sort_order: form.sort_order === "" ? 0 : parseInt(form.sort_order, 10),
        },
        token
      );
    },
    {
      onSuccess: () => {
        onClose();
        onSaved();
      },
      onError: (err) => {
        toast({
          title: "Error saving model",
          description: err?.response?.data?.detail || err?.message || "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (form.sort_order !== "" && isNaN(parseInt(form.sort_order, 10))) e.sort_order = "Must be an integer";
    // Reject 0/negative explicitly: it would be stored as a real cap of "no output at all".
    // Blank is valid and means "provider default".
    if (form.max_output_tokens !== "" && form.max_output_tokens !== null) {
      const maxOut = parseInt(form.max_output_tokens, 10);
      if (isNaN(maxOut)) e.max_output_tokens = "Must be an integer, or blank for the provider default";
      else if (maxOut <= 0) e.max_output_tokens = "Must be greater than 0, or blank for the provider default";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const field = (key, label, opts = {}) => (
    <FormControl mb={3} key={key}>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <Input
        size="sm"
        value={form[key]}
        isReadOnly={opts.readOnly}
        bg={opts.readOnly ? "gray.50" : undefined}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        _hover={{ borderColor: "gray.400" }}
        _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
        _focusVisible={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
      />
      {opts.readOnly && <Text fontSize="xs" color="gray.400" mt={1}>Name cannot be changed after creation.</Text>}
      {errors[key] && <Text fontSize="xs" color="red.500" mt={1}>{errors[key]}</Text>}
    </FormControl>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEditing ? "Edit Model" : "Add Model"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {field("name", "Name (unique identifier)", { readOnly: isEditing })}
          {field("label", "Label (display name)")}
          {field("provider", "Provider (e.g. bedrock, openai)")}
          {field("litellm_model_name", "LiteLLM Model Name")}
          {field("price_per_million_input", "Price per 1M input tokens (USD)")}
          {field("price_per_million_output", "Price per 1M output tokens (USD)")}
          {field("temperature", "Temperature (0.0–1.0; some reasoning models require 1)")}
          {field("max_output_tokens", "Max output tokens (completion only; blank = provider default, usually 4096)")}
          {field("sort_order", "Sort Order")}
          <FormControl mb={3}>
            <FormLabel fontSize="sm">Active</FormLabel>
            <Switch
              isChecked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              colorScheme="green"
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" mr={3} onClick={onClose}>Cancel</Button>
          <Button variant="primary" isLoading={isLoading} onClick={() => { if (validate()) save(); }}>Save</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ModelsAdminSection() {
  const { getAccessTokenSilently } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedModel, setSelectedModel] = useState(null);

  const { data: allModels, refetch } = useQuery({
    queryKey: ["aiModelListAdmin"],
    queryFn: () => getAccessTokenSilently().then((token) => getAllModelsAdmin(token)),
  });

  const handleSaved = () => {
    refetch();
    queryClient.invalidateQueries(["aiModelList"]);
  };

  const handleAdd = () => {
    setSelectedModel(null);
    onOpen();
  };

  const handleEdit = (model) => {
    setSelectedModel(model);
    onOpen();
  };

  const handleDelete = async (model) => {
    if (!window.confirm(`Deactivate model "${model.name}"? It will no longer appear in model selection dropdowns.`)) return;
    try {
      const token = await getAccessTokenSilently();
      await deleteModelAdmin(model.name, token);
      refetch();
      queryClient.invalidateQueries(["aiModelList"]);
    } catch (err) {
      toast({
        title: "Error deactivating model",
        description: err?.response?.data?.detail || err?.message || "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Card mb={4}>
      <HStack justify="space-between" mb={3}>
        <H2 mb={0}>Model Catalog</H2>
        <Button size="sm" variant="primary" leftIcon={<FaPlus />} onClick={handleAdd}>Add Model</Button>
      </HStack>
      <Text fontSize="sm" color="gray.500" mb={3}>
        Manage available LLM models. Inactive models are hidden from selection dropdowns.
      </Text>
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Label</Th>
            <Th>Provider</Th>
            <Th>LiteLLM Model</Th>
            <Th isNumeric>$/M In</Th>
            <Th isNumeric>$/M Out</Th>
            <Th isNumeric>Temp</Th>
            <Th isNumeric>Max Out</Th>
            <Th isNumeric>Order</Th>
            <Th>Status</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {(allModels || []).map((m) => (
            <Tr
              key={m.name}
              opacity={m.active ? 1 : 0.45}
              cursor="pointer"
              _hover={{ bg: "gray.50" }}
              onClick={() => handleEdit(m)}
            >
              <Td fontFamily="mono" fontSize="xs">{m.name}</Td>
              <Td>{m.label}</Td>
              <Td>{m.provider}</Td>
              <Td fontFamily="mono" fontSize="xs">{m.litellm_model_name}</Td>
              <Td isNumeric>{m.price_per_million_input}</Td>
              <Td isNumeric>{m.price_per_million_output}</Td>
              <Td isNumeric>{m.temperature}</Td>
              {/* blank/NULL => provider default, shown as a dash rather than an empty cell */}
              <Td isNumeric>{m.max_output_tokens ?? "—"}</Td>
              <Td isNumeric>{m.sort_order}</Td>
              <Td>
                {m.active
                  ? <Badge colorScheme="green">Active</Badge>
                  : <Badge colorScheme="gray">Inactive</Badge>}
              </Td>
              <Td onClick={(e) => e.stopPropagation()}>
                <IconButton
                  size="xs"
                  variant="ghost"
                  icon={<FaPencilAlt />}
                  aria-label="Edit"
                  mr={1}
                  onClick={() => handleEdit(m)}
                />
                <IconButton
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  icon={<FaTrash />}
                  aria-label="Deactivate"
                  onClick={() => handleDelete(m)}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <ModelModal isOpen={isOpen} onClose={onClose} selectedModel={selectedModel} onSaved={handleSaved} />
    </Card>
  );
}

function SystemDefaultsSection() {
  const { getAccessTokenSilently } = useAuth();
  const toast = useToast();
  const [defaults, setDefaults] = useState({});

  const { data: modelList } = useQuery({
    queryKey: ["aiModelList"],
    queryFn: () => getAccessTokenSilently().then((token) => getModels(token)),
  });
  const modelOptions = (modelList || []).map((m) => m.name);

  const { data: savedDefaults } = useQuery({
    queryKey: ["systemModelDefaults"],
    queryFn: () => getAccessTokenSilently().then((token) => getSystemDefaults(token)),
    onSuccess: (data) => setDefaults(data || {}),
  });

  useEffect(() => {
    if (savedDefaults) setDefaults(savedDefaults);
  }, [savedDefaults]);

  const { mutate: save, isLoading } = useMutation(
    async () => {
      const token = await getAccessTokenSilently();
      return saveSystemDefaults(defaults, token);
    },
    {
      onSuccess: () => {
        toast({ title: "System defaults saved", status: "success", duration: 3000, isClosable: true });
      },
      onError: (err) => {
        toast({
          title: "Error saving system defaults",
          description: err?.response?.data?.detail || err?.message || "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  return (
    <Card mb={4}>
      <H2>System Default Models</H2>
      <Text fontSize="sm" color="gray.500" mb={4}>
        Set the default model for each component. These apply to all organisations that have not configured their own model override. Leave blank to require an explicit choice — chat calls for a component with no default configured will fail.
      </Text>
      {COMPONENT_IDS.map(({ id, label }) => (
        <FormControl key={id} mb={3}>
          <FormLabel fontSize="sm">{label}</FormLabel>
          <Select
            size="sm"
            value={defaults[id] || ""}
            onChange={(e) => setDefaults((d) => ({ ...d, [id]: e.target.value }))}
            _hover={{ borderColor: "gray.400" }}
            _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
            _focusVisible={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
          >
            <option value="">— none (required) —</option>
            {modelOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </Select>
        </FormControl>
      ))}
      <Button variant="primary" mt={2} onClick={() => save()} isLoading={isLoading}>Save Defaults</Button>
    </Card>
  );
}

const EMPTY_EMBEDDING_CONFIG = { provider: "", model: "", dimensions: "" };

function EmbeddingConfigSection() {
  const { getAccessTokenSilently } = useAuth();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_EMBEDDING_CONFIG);
  const [reindexTaskId, setReindexTaskId] = useState(null);

  const { data: savedConfig } = useQuery({
    queryKey: ["embeddingConfig"],
    queryFn: () => getAccessTokenSilently().then((token) => getEmbeddingConfig(token)),
  });

  useEffect(() => {
    if (savedConfig) {
      setForm({
        provider: savedConfig.provider ?? "",
        model: savedConfig.model ?? "",
        dimensions: savedConfig.dimensions ?? "",
      });
    }
  }, [savedConfig]);

  const { mutate: save, isLoading: isSaving } = useMutation(
    async () => {
      const token = await getAccessTokenSilently();
      return saveEmbeddingConfig(
        { ...form, dimensions: form.dimensions === "" ? 0 : parseInt(form.dimensions, 10) },
        token
      );
    },
    {
      onSuccess: () => {
        toast({ title: "Embedding config saved", status: "success", duration: 3000, isClosable: true });
      },
      onError: (err) => {
        toast({
          title: "Error saving embedding config",
          description: err?.response?.data?.detail || err?.message || "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  const { mutate: reindex, isLoading: isReindexing } = useMutation(
    async () => {
      const token = await getAccessTokenSilently();
      return triggerWeaviateReindex({}, token);
    },
    {
      onSuccess: (data) => {
        setReindexTaskId(data.task.id);
        toast({ title: "Reindex started", status: "info", duration: 3000, isClosable: true });
      },
      onError: (err) => {
        toast({
          title: "Error triggering reindex",
          description: err?.response?.data?.detail || err?.message || "Unknown error",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  const field = (key, label) => (
    <FormControl mb={3} key={key}>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <Input
        size="sm"
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        _hover={{ borderColor: "gray.400" }}
        _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
        _focusVisible={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
      />
    </FormControl>
  );

  return (
    <Card mb={4}>
      <H2>Embedding Config</H2>
      <Text fontSize="sm" color="gray.500" mb={3}>
        The single embedding model used across the whole vector DB. Changing this does NOT
        automatically re-embed existing data — after saving, click "Trigger Reindex" (or it will
        happen automatically, blocking, on the next RAG query).
      </Text>
      {field("provider", "Provider (e.g. openai)")}
      {field("model", "Model (e.g. text-embedding-3-small)")}
      {field("dimensions", "Dimensions (e.g. 512)")}
      <HStack mt={2}>
        <Button variant="primary" isLoading={isSaving} onClick={() => save()}>Save Embedding Config</Button>
        <Button variant="secondary" isLoading={isReindexing} onClick={() => reindex()}>Trigger Reindex</Button>
      </HStack>
      {reindexTaskId && (
        <HStack mt={3}>
          <Text fontSize="sm" color="gray.500">Reindex progress:</Text>
          <AIProgressWidget taskId={reindexTaskId} />
        </HStack>
      )}
    </Card>
  );
}

function ModelCatalog({ componentRequiredPermissions }) {
  return (
    <Page title="Model Catalog" componentRequiredPermissions={componentRequiredPermissions}>
      <ModelsAdminSection />
      <SystemDefaultsSection />
      <EmbeddingConfigSection />
    </Page>
  );
}

export default WithAuthorization(ModelCatalog);
